import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { claimRedeemProduct, fetchRedeemProducts } from "../../api/redeem.api";
import { fetchProfileDashboard } from "../../api/profile.api";
import { useAuth } from "../../context/AuthContext";
import "./Redeempage.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const buildImageUrl = (imagePath) => {
  if (!imagePath) return "";
  if (/^https?:\/\//i.test(imagePath)) return imagePath;

  const base = API_BASE_URL.endsWith("/")
    ? API_BASE_URL.slice(0, -1)
    : API_BASE_URL;

  if (imagePath.startsWith("/")) {
    return `${base}${imagePath}`;
  }

  return `${base}/${imagePath}`;
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString();
};

const normalizeProducts = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.products)) return data.products;
  return [];
};

const RedeemDetailPage = () => {
  const navigate = useNavigate();
  const { itemId } = useParams();
  const parsedId = Number(itemId);
  const { isAuthenticated, setShowLoginModal } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimStatus, setClaimStatus] = useState({ type: "", message: "" });
  const [userPoints, setUserPoints] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    if (!Number.isFinite(parsedId)) {
      setError("Invalid redeem item.");
      setLoading(false);
      return;
    }

    fetchRedeemProducts()
      .then((data) => {
        if (!mounted) return;

        const list = normalizeProducts(data).filter(
          (item) => item && item.is_active !== false
        );
        const found = list.find((item) => Number(item.id) === parsedId) || null;

        if (!found) {
          setError("Redeem item not found.");
          setProduct(null);
          return;
        }

        setProduct(found);
      })
      .catch(() => {
        if (!mounted) return;
        setError("Unable to load redeem item details right now.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [parsedId]);

  useEffect(() => {
    let mounted = true;

    if (!isAuthenticated) {
      setUserPoints(null);
      return () => {
        mounted = false;
      };
    }

    setPointsLoading(true);

    fetchProfileDashboard()
      .then((data) => {
        if (!mounted) return;
        setUserPoints(Number(data?.points_balance) || 0);
      })
      .catch(() => {
        if (!mounted) return;
        setUserPoints(null);
      })
      .finally(() => {
        if (mounted) setPointsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  const canClaim = useMemo(() => {
    if (!product) return false;
    if (claimed) return false;
    return Number(product.stock) > 0;
  }, [claimed, product]);
  const description = String(product?.description || "").trim();
  const displayPoints = Number(product?.points_required);
  const showDisplayPoints = Number.isFinite(displayPoints) && displayPoints > 0;
  const previousPrice = Number(
    product?.cost_price ?? product?.mrp ?? product?.original_price ?? product?.previous_price
  );
  const currentPrice = Number(product?.price ?? product?.selling_price);
  const showPreviousPrice =
    Number.isFinite(previousPrice) &&
    Number.isFinite(currentPrice) &&
    previousPrice > currentPrice &&
    currentPrice > 0;

  const handleClaim = async () => {
    if (!product) return;

    setClaimStatus({ type: "", message: "" });

    const requiredPoints = Number(product.points_required) || 0;

    if (!isAuthenticated) {
      setClaimStatus({
        type: "error",
        message: "Please sign in to claim this redeem item.",
      });
      setShowLoginModal(true);
      return;
    }

    if (pointsLoading) {
      setClaimStatus({
        type: "error",
        message: "Checking your Rewards balance. Please try again in a moment.",
      });
      return;
    }

    if (userPoints === null) {
      setClaimStatus({
        type: "error",
        message:
          "Unable to verify your Rewards right now. Please refresh and try again.",
      });
      return;
    }

    if (userPoints <= 250) {
      setClaimStatus({
        type: "error",
        message:
          "You need more than ₹250 Rewards to claim redeem products. Keep shopping to earn more.",
      });
      return;
    }

    if (userPoints < requiredPoints) {
      setClaimStatus({
        type: "error",
        message: `You need ₹${requiredPoints} Rewards, but you currently have ₹${userPoints} Rewards.`,
      });
      return;
    }

    setClaiming(true);

    try {
      const result = await claimRedeemProduct(product.id);

      if (result.status !== 200) {
        throw new Error("Claim request did not return 200.");
      }

      setClaimed(true);
      setProduct((prev) =>
        prev
          ? {
              ...prev,
              stock: Math.max(Number(prev.stock || 0) - 1, 0),
            }
          : prev
      );
      setUserPoints((prev) =>
        typeof prev === "number" ? Math.max(prev - requiredPoints, 0) : prev
      );
      setClaimStatus({ type: "success", message: "Order placed / Claimed" });
    } catch (err) {
      const fallback = "Unable to claim this product.";
      const message =
        err?.status === 401
          ? "Please sign in to claim this redeem item."
          : err?.message || fallback;
      setClaimStatus({ type: "error", message });
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <section className="page">
        <div className="loading-wrap">
          <div className="loader" />
          <div>Loading item details...</div>
        </div>
      </section>
    );
  }

  if (error || !product) {
    return (
      <section className="page">
        <div className="redeem-breadcrumb">
          <span onClick={() => navigate("/")}>Home</span>
          <span>/</span>
          <span onClick={() => navigate("/Redeempage")}>Redeempage</span>
          <span>/</span>
          <span>Details</span>
        </div>

        <div className="redeem-box redeem-centered redeem-error">
          {error || "Redeem item not found."}
        </div>
      </section>
    );
  }

  return (
    <section className="page">
      <div className="breadcrumb11">
        <div className="page-container">
          <span className="breadcrumb-link" onClick={() => navigate("/")}>Home</span>
          <span className="highlight"> / </span>
          <span
           onClick={() => navigate("/Redeempage")}
            className="breadcrumb-link"
          >
            Redeempage
          </span>
          <span className="highlight"> / </span>
           <span >{product.name}</span>

        </div>
      </div>

      <div className="redeem-detail-shell">
        <div className="redeem-detail">
          <div className="redeem-detail-image">
            {product.image ? (
              <img src={buildImageUrl(product.image)} alt={product.name} />
            ) : (
              <div className="redeem-fallback">No image</div>
            )}
          </div>

          <div className="redeem-detail-content">
            <p className="redeem-detail-label">
              {product.slug ? product.slug.toUpperCase() : "REDEEM ITEM"}
            </p>
            <h2>{product.name}</h2>

            {showDisplayPoints && (
              <p className="redeem-points-big">₹{displayPoints} Rewards</p>
            )}

            {showPreviousPrice && (
              <div className="redeem-price-row">
                <span className="redeem-prev-price">₹{previousPrice}</span>
                <span className="redeem-current-price">₹{currentPrice}</span>
              </div>
            )}

            <div className="redeem-points-note">
              Redeem NOW for FREE !!
            </div>

            <p className="redeem-available-points">
              {isAuthenticated
                ? pointsLoading
                  ? "Your Rewards: checking..."
                  : `Your Rewards: ₹${userPoints ?? "N/A"}`
                : "Sign in to view your Rewards and claim this item."}
            </p>

            <div className="redeem-meta">
              <span>Added: {formatDate(product.created_at)}</span>
            </div>

            <div className="redeem-detail-actions">
              <button
                type="button"
                className={`redeem-claim-btn ${claiming ? "loading" : ""}`}
                onClick={handleClaim}
                disabled={!canClaim || claiming}
                aria-busy={claiming}
              >
                {claiming ? (
                  <span className="redeem-btn-loading">
                    <span className="redeem-spinner" aria-hidden="true" />
                    <span>Claiming...</span>
                  </span>
                ) : claimed ? (
                  "Claimed"
                ) : (
                  "Claim Now"
                )}
              </button>
            </div>

            {claimStatus.message && (
              <p
                className={`redeem-status ${claimStatus.type === "success" ? "success" : "error"
                  }`}
              >
                {claimStatus.message}
              </p>
            )}

            {description && (
              <>
                <div className="redeem-detail-divider" />
                <div className="redeem-description-wrap">
                  <h3>Redeem Details</h3>
                  <p className="redeem-description">{description}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RedeemDetailPage;
